import * as React from "react";
import { TableCell } from "@mui/material";
import { DataTable, type DataTableColumn } from "@/components/dashboard/DataTable";
import { QuantityCell } from "@/components/dashboard/QuantityCell";
import { PriceCell } from "@/components/dashboard/PriceCell";
import { PositionBadge } from "@/components/dashboard/PositionBadge";

export interface PositionRow {
  execution_id: string;
  instrument_token: string;
  side?: string | null;
  quantity: number;
  average_price?: number | null;
  entry_time?: string | null;
  status?: string | null;
}

interface PositionTableProps {
  positions: PositionRow[];
}

export function PositionTable({ positions }: PositionTableProps): React.JSX.Element {
  const columns: Array<DataTableColumn<PositionRow>> = [
    {
      key: "symbol",
      label: "Symbol",
      sortable: true,
      sortValue: (row) => row.instrument_token,
      render: (row) => <TableCell key="symbol">{row.instrument_token}</TableCell>,
    },
    {
      key: "side",
      label: "Side",
      sortable: true,
      sortValue: (row) => row.side ?? "",
      render: (row) => (
        <TableCell key="side">
          {row.side || <span style={{ fontStyle: "italic", opacity: 0.6 }}>Not reported by backend</span>}
        </TableCell>
      ),
    },
    {
      key: "quantity",
      label: "Quantity",
      align: "right",
      sortable: true,
      sortValue: (row) => row.quantity,
      render: (row) => <QuantityCell key="quantity" value={row.quantity} />,
    },
    {
      key: "average_price",
      label: "Average Price",
      align: "right",
      sortable: true,
      sortValue: (row) => row.average_price ?? 0,
      render: (row) => <PriceCell key="average_price" value={row.average_price} />,
    },
    {
      key: "entry_time",
      label: "Entry Time",
      sortable: true,
      sortValue: (row) => row.entry_time ?? "",
      render: (row) => (
        <TableCell key="entry_time">
          {row.entry_time ?? <span style={{ fontStyle: "italic", opacity: 0.6 }}>Not reported by backend</span>}
        </TableCell>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <TableCell key="status">
          <PositionBadge status={row.status} />
        </TableCell>
      ),
    },
  ];

  return (
    <DataTable
      ariaLabel="Open Positions"
      columns={columns}
      rows={positions}
      getRowKey={(row) => row.execution_id}
    />
  );
}
